"use client";

import { useCallback, useEffect, useState } from "react";

/**
 * Tracks an element's rendered width. Uses a callback ref, so it starts measuring whenever the
 * element mounts, including after an empty state.
 * @returns A ref callback to attach and the width in pixels (0 before the first measure).
 * @throws Never.
 */
export function useElementWidth<T extends HTMLElement>(): { ref: (element: T | null) => void; width: number } {
  // States
  const [element, setElement] = useState<T | null>(null);
  const [width, setWidth] = useState(0);

  // Handlers
  const ref = useCallback((node: T | null) => setElement(node), []);

  // Effects
  useEffect(() => {
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return { ref, width };
}
